import { AREA_PARAMETER_NAME, TONNE_UNIT_CODE } from 'constants/gcConstants';
import {
  GUnit,
  UnitCategory,
  UnitParameterType,
} from 'interfaces/IGlobalCatalogue';
import { IUnit } from 'interfaces/IInput';

export interface GUnitFilter {
  parameters: string[];
  category: UnitCategory;
}

// GUnit.snapParameter data is inconsistent about which dash character
// separates the category/subcategory (e.g. real records use an en dash,
// U+2013, while our constants use a hyphen-minus), so parameter matching
// normalises the whole Unicode hyphen/dash block (U+2010-U+2015) instead of
// relying on exact string equality.
export const normalizeParameterLabel = (value: string): string =>
  value
    .replace(/\s*[-‐-―]\s*/g, ' - ')
    .trim()
    .toLowerCase();

export const filterGUnitsByFilter = (
  gUnits: GUnit[],
  filter: GUnitFilter
): GUnit[] => {
  const normalizedTargets = filter.parameters.map(normalizeParameterLabel);
  return gUnits.filter(
    (unit) =>
      normalizedTargets.includes(normalizeParameterLabel(unit.snapParameter)) &&
      (unit.category & filter.category) === filter.category
  );
};

export const AREA_UNIT_FILTER: GUnitFilter = {
  parameters: [AREA_PARAMETER_NAME],
  category: UnitCategory.General,
};

export const PRODUCTION_UNIT_FILTER: GUnitFilter = {
  parameters: [UnitParameterType.PlantProductionLargeSolid],
  category: UnitCategory.Production,
};

/** The Production tonne ("t") GUnit as a step unit, or null until GUnits load. */
export const findProductionTonneUnit = (gUnits: GUnit[]): IUnit | null => {
  const tonne = filterGUnitsByFilter(gUnits, PRODUCTION_UNIT_FILTER).find(
    (unit) => unit.code === TONNE_UNIT_CODE
  );
  return tonne ? { unit: tonne.code, globalId: tonne.globalId } : null;
};

/** Used when the org has no Production "t" GUnit, so the stored unit still reads as tonnes. */
export const PRODUCTION_TONNE_UNIT_FALLBACK: IUnit = {
  unit: TONNE_UNIT_CODE,
  globalId: null,
};

/** The Production "t" GUnit, or the bare "t" fallback when the org has none. */
export const resolveProductionTonneUnit = (gUnits: GUnit[]): IUnit =>
  findProductionTonneUnit(gUnits) ?? PRODUCTION_TONNE_UNIT_FALLBACK;
